import { useMemo } from 'react';

import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';

import { CissNumberField } from 'components/fields';
import { PanelGrid } from 'components/grid';
import { ConfigPedidoRemoveBtn } from 'pages/compra/configuracao-pedido/components/form/ConfigPedidoRemoveBtn';
import { useConfigPedidoListContext } from 'pages/compra/configuracao-pedido/contexts';
import { ConfigPedidoImportarEstoque, ProdutoEstoque } from 'pages/compra/configuracao-pedido/types';

type ConfigPedidoImportarEstoqueGrid = {
    handleExcluir: (idList: Set<GridRowId>) => void;
};

/**
 * Transforma os produtos em linhas para o grid.
 */
const getRows = (products: ProdutoEstoque[] = []) =>
    products.map((item, index) => ({
        id: item.idProduto,
        index,
        idProduto: item.idProduto,
        cdProduto: item.cdProduto || '',
        dsProduto: item.dsProduto,
        qtEstoqueAtual: item.qtEstoqueAtual,
        qtEstoqueTotal: item.qtEstoqueTotal,
        fgFracionavel: item.produto?.unidadeMedida?.fgFracionavel ?? item.fgFracionavel ?? false,
    }));

type GridRows = ReturnType<typeof getRows>;

/**
 * Filtra os produtos com base no valor do filtro.
 */
const getFilteredRows = (products: GridRows, valueFilter: string) =>
    products.filter(
        (item) =>
            !valueFilter ||
            item.dsProduto.toLowerCase().includes(valueFilter.trim().toLowerCase()) ||
            item.cdProduto.toString().includes(valueFilter.trim().toLowerCase()),
    );

export function ConfigPedidoImportarEstoqueGrid({ handleExcluir }: ConfigPedidoImportarEstoqueGrid) {
    const { values, setFieldValue } = useFormikContext<ConfigPedidoImportarEstoque>();
    const { valueFilter, setSelectedRows } = useConfigPedidoListContext();

    const rows = useMemo(() => {
        const rows = getRows(values.configPedidoImportarEstoque);

        return getFilteredRows(rows, valueFilter);
    }, [values.configPedidoImportarEstoque, valueFilter]);

    const columns = useMemo(
        (): GridColDef[] => [
            { field: 'cdProduto', headerName: 'Código', minWidth: 130 },
            { field: 'dsProduto', headerName: 'Descrição', flex: 2, minWidth: 120 },
            {
                field: 'qtEstoqueTotal',
                headerName: 'Quantidade Total',
                minWidth: 150,
                flex: 1,
                renderCell: ({ row }) => (
                    <CissNumberField
                        allowNegative={false}
                        inputType={row.fgFracionavel ? 'decimal' : 'number'}
                        value={row.qtEstoqueTotal}
                        onBlur={(e) => setFieldValue(`configPedidoImportarEstoque.${row.index}.qtEstoqueTotal`, Number(e.target.value))}
                        size="small"
                    />
                ),
            },
            { field: 'qtEstoqueAtual', headerName: 'Quantidade Disponível', minWidth: 90 },
            {
                field: 'acoes',
                align: 'center',
                headerAlign: 'center',
                headerName: 'Ações',
                filterable: false,
                sortable: false,
                hideable: false,
                disableColumnMenu: true,
                minWidth: 70,
                renderCell: ({ row }) => <ConfigPedidoRemoveBtn onClick={() => handleExcluir(new Set([row.idProduto]))} />,
            },
        ],
        [values.configPedidoImportarEstoque],
    );

    return <PanelGrid rows={rows} columns={columns} loading={false} reload={false} remote={false} checkboxSelection onClickRow={setSelectedRows} />;
}
